import { useState } from 'react';
import { Link } from 'react-router-dom';
import taxData from '../data/tax-reference.json';

const fmt = (n: number) => `$${n.toLocaleString()}`;

export default function TaxReference() {
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const years = taxData.map((d) => d.year).sort((a, b) => b - a);
  void (selectedYear); // Used for column highlighting

  return (
    <div className="bg-white min-h-screen flex flex-col">
      {/* Nav */}
      <nav className="border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <Link to="/">
              <img src="/branding/filie-logo-full.svg" alt="Filie" className="h-7" />
            </Link>
            <div className="flex items-center space-x-6">
              <Link to="/updates" className="text-sm text-gray-500 hover:text-gray-800 transition-colors">Updates</Link>
              <Link to="/tax-reference" className="text-sm text-gray-900 font-medium">Tax Reference</Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex-1 max-w-5xl mx-auto w-full px-6 lg:px-8 py-12">
        <div className="mb-10">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Reference</p>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Tax Numbers by Year</h1>
          <p className="text-gray-500 text-sm">
            Key federal tax thresholds, limits, and credit amounts — 2020 through 2026.
          </p>
        </div>

        {/* Comparison Table (all years) */}
        <div className="overflow-x-auto mb-12">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 pr-4 text-xs text-gray-400 uppercase tracking-wide font-medium sticky left-0 bg-white">Metric</th>
                {years.map((y) => (
                  <th
                    key={y}
                    className={`text-right py-3 px-3 text-xs uppercase tracking-wide font-medium cursor-pointer transition-colors ${
                      selectedYear === y ? 'text-gray-900 bg-gray-50' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    onClick={() => setSelectedYear(selectedYear === y ? null : y)}
                  >
                    {y}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { label: 'Std Deduction (Single)', key: 'standardDeduction', sub: 'single' },
                { label: 'Std Deduction (MFJ)', key: 'standardDeduction', sub: 'mfj' },
                { label: 'Std Deduction (HoH)', key: 'standardDeduction', sub: 'hoh' },
                { label: 'Top Bracket Starts (Single)', key: 'topBracket', sub: 'single' },
                { label: 'Top Bracket Starts (MFJ)', key: 'topBracket', sub: 'mfj' },
                { label: 'SS Wage Base', key: 'ssWageBase' },
                { label: 'Child Tax Credit', key: 'childTaxCredit' },
                { label: 'EITC Max (3+ kids)', key: 'eitcMax3' },
                { label: 'HSA Limit (Self)', key: 'hsaSelf' },
                { label: 'HSA Limit (Family)', key: 'hsaFamily' },
                { label: 'IRA Contribution Limit', key: 'iraLimit' },
                { label: 'SALT Deduction Cap', key: 'saltCap' },
                { label: 'Mortgage Interest Cap', key: 'mortgageCap' },
                { label: 'Foreign Earned Income', key: 'foreignEarnedIncome' },
                { label: 'Estate Tax Exemption', key: 'estateExemption' },
                { label: 'Gift Tax Exclusion', key: 'giftExclusion' },
              ].map((row) => (
                <tr key={row.label} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="py-2.5 pr-4 text-gray-700 font-medium text-xs sticky left-0 bg-white">{row.label}</td>
                  {years.map((y) => {
                    const yearData = taxData.find((d) => d.year === y);
                    if (!yearData) return <td key={y} />;
                    // The reference JSON is loosely shaped; index it by the row's key name.
                    const yr = yearData as unknown as Record<string, number | Record<string, number> | undefined>;
                    const cell = yr[row.key];
                    const val = row.sub && typeof cell === 'object' && cell !== null
                      ? cell[row.sub]
                      : (typeof cell === 'number' ? cell : undefined);
                    // Highlight changes from previous year
                    const prevYear = taxData.find((d) => d.year === y - 1);
                    const pyr = prevYear as unknown as Record<string, number | Record<string, number> | undefined> | undefined;
                    const prevCell = pyr?.[row.key];
                    const prevVal = pyr
                      ? row.sub && typeof prevCell === 'object' && prevCell !== null
                        ? prevCell[row.sub]
                        : (typeof prevCell === 'number' ? prevCell : undefined)
                      : null;
                    const changed = prevVal !== null && prevVal !== val;
                    return (
                      <td
                        key={y}
                        className={`py-2.5 px-3 text-right text-xs tabular-nums ${
                          selectedYear === y ? 'bg-gray-50 font-medium text-gray-900' : 'text-gray-600'
                        } ${changed ? 'text-gray-900 font-medium' : ''}`}
                      >
                        {val !== undefined ? fmt(val) : '—'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Year Detail (Notable Changes) */}
        <div className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-6">Notable Changes by Year</h2>
          <div className="space-y-6">
            {years.map((y) => {
              const yearData = taxData.find((d) => d.year === y);
              if (!yearData || !yearData.notes || yearData.notes.length === 0) return null;
              return (
                <div key={y} className="flex">
                  <div className="w-16 flex-shrink-0">
                    <span className="text-sm font-bold text-gray-900">{y}</span>
                  </div>
                  <ul className="flex-1 space-y-1">
                    {yearData.notes.map((note: string, i: number) => (
                      <li key={i} className="text-sm text-gray-600 flex items-start">
                        <span className="text-gray-300 mr-2 mt-0.5">•</span>
                        <span>{note}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="border-t border-gray-100 pt-6">
          <p className="text-xs text-gray-400">
            Sources: IRS Revenue Procedures, Tax Foundation, SSA. Amounts are for federal taxes only.
            State taxes vary. This is for informational purposes — consult a tax professional for advice specific to your situation.
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8 px-6">
        <div className="max-w-5xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img src="/branding/filie-icon.svg" alt="Filie" className="w-5 h-5" />
            <span className="font-semibold text-gray-900 text-sm">Filie</span>
            <span className="text-gray-300 text-sm">&middot;</span>
            <span className="text-gray-400 text-sm">Know your taxes.</span>
          </div>
          <p className="text-xs text-gray-300">© 2026 Filie</p>
        </div>
      </footer>
    </div>
  );
}
